/**
 * Reversi 盤 (8x8) コンポーネント。 CSS Grid 描画。
 *
 * - `state` を見て描画
 * - `legal` (合法手列) があれば、 自分の手番のときだけハイライト + クリック可能
 * - クリック → `onMove({ Place: { row, col } })`
 * - `lastMove` があるセルにリングを表示。
 */

import { type RefObject, useRef } from 'react';
import { isPlace, legalKey, type ReversiMove, type ReversiState, type Side } from '../../games/reversi';
import { useFirstPaint, useFreshLastMove } from '../../lib/first-paint';
import { useUiBusyAnim } from '../../lib/ui-busy';
import { BoardCoords } from './BoardCoords';
import { QuietPlacementTools } from './QuietPlacementTools';

interface Props {
  state: ReversiState;
  legal?: ReversiMove[];
  yourSide?: Side | null;
  /** _view.allowed_boards が現時点で着手を許可しているか。 未指定時は engine cs ベースの
   *  従来挙動 (= FullyParallel / 単盤では問題ないが、 Parallel 多盤で乖離するので
   *  Play.tsx からは必ず渡す)。 */
  myTurnAllowed?: boolean;
  onMove?: (mv: ReversiMove) => void;
  /** ミニ盤として描画 (クリック無効、サイズ縮小)。 */
  mini?: boolean;
  /** 直前の一手 (リングで強調)。 */
  lastMove?: ReversiMove;
  /** Replay / Archive 等の静的ビューでは「相手の手番」 表示を出さない。 */
  staticView?: boolean;
  /** チュートリアル用の強調マス。 */
  tutorialHighlight?: [number, number][];
}

export function ReversiBoard({
  state,
  legal,
  yourSide,
  myTurnAllowed,
  onMove,
  mini,
  lastMove,
  staticView,
  tutorialHighlight,
}: Props) {
  // 盤ごと mount した最初の描画では、 石を置くアニメも直前手のリングも出さない
  // (lib/first-paint.ts)。 この後に置かれた/返った石だけがアニメする。
  const firstPaint = useFirstPaint();
  const freshLast = useFreshLastMove(lastMove);

  // myTurnAllowed が指定されていればそれを真とし、 未指定 (= 旧 caller / mini) は
  // engine cs ベースにフォールバック。
  const isMyTurn =
    myTurnAllowed !== undefined
      ? myTurnAllowed
      : yourSide != null && yourSide === state.side;
  const legalSet = new Set(
    (legal ?? [])
      .filter(isPlace)
      .map((m) => legalKey(m.Place.row, m.Place.col)),
  );
  const hasPass = (legal ?? []).some((m) => m === 'Pass');
  const onlyPass = isMyTurn && hasPass && legalSet.size === 0;
  const lastKey = lastMove && isPlace(lastMove)
    ? legalKey(lastMove.Place.row, lastMove.Place.col)
    : null;
  // 着手 side のドット色: Host = 黒、 Guest = 白
  const sideClass = state.side === 'First' ? 'side-black' : 'side-white';

  // Reversi は state に outcome を持たない (passes>=2 で終局判定)。 game 終了は
  // upstream (Play 側) で別ハンドリングなので、 ここでは isMyTurn=false なら hint。
  const showOppHint = !mini && !staticView && !isMyTurn;

  // 盤を frame で包むのは他ゲーム (チェス / 将棋 / 囲碁 / 連珠) と同じ規約。
  // `.reversi-board` の padding は % 指定 (焼いた盤画像の余白 14mm に合わせてある) で、
  // % は**親の幅**に対して解決される。 親が盤より広いと余白が過大になり、 升が
  // 焼いた罫からずれて石が枡目に乗らなくなる。 幅が盤と一致する箱で包んで防ぐ。
  return (
    <div className={`reversi-frame ${mini ? 'mini' : ''}`}>
      <QuietPlacementTools game="Reversi" yourSide={yourSide} mini={mini} staticView={staticView} />
      <div
        className={`reversi-board ${mini ? 'mini' : ''} ${sideClass} ${showOppHint ? 'not-my-turn' : ''}`}
      >
        {/* 座標は升の外 (盤の縁) に。 升の中だと石に隠れる。 リバーシは盤を反転しない。 */}
        {!mini && (
          <BoardCoords
            files={Array.from({ length: 8 }, (_, c) => String.fromCharCode(97 + c))}
            ranks={Array.from({ length: 8 }, (_, r) => `${r + 1}`)}
          />
        )}
        {state.board.flatMap((row, r) =>
          row.map((cell, c) => {
            const k = legalKey(r, c);
            const isLegal = isMyTurn && legalSet.has(k);
            const isLast = !mini && lastKey === k;
            const isTutorialHint =
              tutorialHighlight?.some(([hr, hc]) => hr === r && hc === c) ?? false;
            const handler = () => {
              if (isLegal) onMove?.({ Place: { row: r, col: c } });
            };
            return (
              <button
                key={`${r}-${c}`}
                className={`cell ${cell === 'Black' ? 'black' : cell === 'White' ? 'white' : ''} ${
                  isLegal ? 'legal' : ''
                } ${isLast ? 'last-move' : ''} ${isLast && !freshLast ? 'no-anim' : ''} ${isTutorialHint ? 'tutorial-hint' : ''}`}
                onClick={handler}
                disabled={mini || !isLegal}
                aria-label={`row ${r} col ${c}`}
              >
                {/* key=cell で 色変更 (= 返り) のたびに span を remount。
                    .stone の CSS アニメ stone-place-in がそのたびに走り、
                    返された駒が「置き直される」 視覚効果になる。
                    AnimatedStone で global ui-busy counter に登録し、 CPU の
                    pickMove 起動を「アニメ完了後」 にゲートする。 */}
                {cell !== 'Empty' && <AnimatedStone key={cell} firstPaint={firstPaint} />}
              </button>
            );
          }),
        )}
      </div>
      {!mini && onlyPass && (
        <button
          className="reversi-pass-btn"
          onClick={() => onMove?.('Pass')}
          data-testid="reversi-pass"
        >
          パス (合法手なし)
        </button>
      )}
      {showOppHint && (
        <div className="board-turn-hint" aria-hidden>
          手番待ち
        </div>
      )}
    </div>
  );
}

/** Reversi の駒。 mount 時の stone-place-in アニメを global ui-busy に登録して
 *  CPU 起動側がアニメ完了まで待てるようにする。 */
function AnimatedStone({ firstPaint }: { firstPaint: RefObject<boolean> }) {
  // 生成時点の値で固定する (後で false に変わっても、 この石はアニメしないまま)。
  const noAnim = useRef(firstPaint.current).current;
  const ref = useRef<HTMLSpanElement>(null);
  useUiBusyAnim(ref);
  return <span ref={ref} className={`stone${noAnim ? ' no-anim' : ''}`} />;
}
